import React, { useMemo } from 'react';
import { useSelect } from 'downshift';
import classNames from 'classnames';

import { Icon } from 'js/components';
import { COMBINED_LABEL } from 'js/utils/assessment-cohorts';

import orgPickerStyles from 'js/components/OrgNodePicker/OrgNodePicker.module.scss';
import styles from './BreakoutPicker.module.scss';

export const COMBINED_BREAKOUT_ID = '';

function combinedItem() {
  return { id: COMBINED_BREAKOUT_ID, name: COMBINED_LABEL };
}

function itemToString(item) {
  return item?.name ?? '';
}

function BreakoutPicker({
  breakouts = [],
  selectedId = COMBINED_BREAKOUT_ID,
  onSelect = () => {},
  disabled = false,
  className,
}) {
  const items = useMemo(
    () => [
      combinedItem(),
      ...breakouts.map((breakout) => ({
        id: String(breakout.id),
        name: breakout.name,
      })),
    ],
    [breakouts]
  );
  const selectedItem =
    items.find((item) => item.id === String(selectedId ?? '')) ?? items[0];
  const isDisabled = disabled || items.length === 0;

  const {
    isOpen,
    getToggleButtonProps,
    getMenuProps,
    highlightedIndex,
    getItemProps,
  } = useSelect({
    items,
    selectedItem,
    itemToString,
    onSelectedItemChange: ({ selectedItem: next }) => {
      if (next) {
        onSelect(next.id);
      }
    },
  });

  return (
    <div
      className={classNames(
        orgPickerStyles.root,
        orgPickerStyles.compact,
        className
      )}
    >
      <button
        type="button"
        className={orgPickerStyles['toggle-button']}
        {...getToggleButtonProps({
          disabled: isDisabled,
          'aria-label': 'Breakout',
        })}
      >
        <span className={styles.toggleValue}>
          <span className={orgPickerStyles.viewing}>Breakout</span>
          <h2 className={orgPickerStyles['node-name']}>
            {selectedItem?.name ?? COMBINED_LABEL}
          </h2>
        </span>
        <Icon
          icon={['far', isOpen ? 'chevron-up' : 'chevron-down']}
          fixedWidth
          className="icon icon-right"
        />
      </button>
      <ul
        {...getMenuProps()}
        className={classNames(styles.menu, { [styles.menuOpen]: isOpen })}
      >
        {!isOpen
          ? null
          : items.map((item, index) => (
              <li
                key={item.id || 'combined'}
                className={classNames(styles.item, {
                  [styles.highlighted]: highlightedIndex === index,
                  [styles.selected]: selectedItem?.id === item.id,
                })}
                {...getItemProps({ item, index })}
              >
                {item.name}
              </li>
            ))}
      </ul>
    </div>
  );
}

export default BreakoutPicker;
